import { useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Footer from "../components/landing/Footer";
import { marketingOrigin } from "../data/seo/siteEntity";
import LandingNavbar from "../components/landing/LandingNavbar";
import usePageSeo from "../hooks/usePageSeo";
import JsonLd from "../components/seo/JsonLd";
import FAQSection from "../components/seo/FAQSection";
import { SHOPIFY_DOCS_FAQS } from "../data/seo/faqs";
import { buildServiceSchema } from "../data/seo/structuredData";

type TocLink = { id: string; label: string };

const tocLinks: TocLink[] = [
    { id: "install", label: "1. Install the app" },
    { id: "scopes", label: "2. OAuth scopes" },
    { id: "embed", label: "3. Enable the storefront widget" },
    { id: "dashboard", label: "4. Open the full dashboard" },
    { id: "customize", label: "5. Customize the widget" },
    { id: "tools", label: "6. AI tools in voice & chat" },
    { id: "triggers", label: "7. Outbound triggers" },
    { id: "webhooks", label: "8. Webhooks & data flow" },
    { id: "protected-customer-data", label: "9. Protected customer data" },
    { id: "privacy", label: "10. Privacy & GDPR" },
    { id: "troubleshooting", label: "11. Troubleshooting" },
    { id: "faq", label: "FAQ" },
];

// Must mirror the scopes on the *published* app version, which is what a
// reviewer or merchant actually consents to — not `shopify.app.toml`, which can
// run ahead of it between deploys. A scope in the manifest but not yet in the
// live version is `pending`; claiming it outright reads as access we hold and
// don't. Clear the flag in the same change that deploys the version.
const requestedScopes = [
    { scope: "read_orders", what: "Order lookup, status, line items" },
    { scope: "read_customers", what: "Customer 360 — past orders, contact details" },
    { scope: "read_fulfillments", what: "Live shipment status and tracking URLs" },
    { scope: "read_returns", what: "Return eligibility and existing return cases" },
    { scope: "read_products", what: "Product search, attributes, variants, pricing" },
    { scope: "read_inventory", what: "Filter recommendations by in-stock items" },
    { scope: "read_discounts", what: "Quote active discount codes during a call/chat" },
    { scope: "read_checkouts", what: "Abandoned-checkout recovery triggers" },
    { scope: "read_content", what: "Pages and blog content for knowledge grounding" },
    { scope: "read_legal_policies", what: "Refund/return, shipping, privacy & terms policies" },
    {
        scope: "read_themes",
        what: "Detect whether the app embed is live in the published theme. Read-only, and limited to config/settings_data.json on the main theme.",
        pending: true,
    },
];

// Field-level justification for Shopify's protected customer data review. Each
// row names the feature that consumes the field; a field nothing consumes must
// not appear here, and must not be selectable in the integration panel either.
//
// `anchor` is cited as the proof-of-resolution URL for that field's own review
// item, so these ids are external contracts — renaming one breaks the link a
// reviewer was given. Append rather than renumber.
const protectedDataUses = [
    {
        anchor: "data-order",
        field: "Order number & order contents",
        where: "shopify → get_order / list_orders",
        why: "Answers \"where is my order\" and \"what did I buy\" with the shopper's own line items, totals and payment status.",
    },
    {
        anchor: "data-tracking",
        field: "Fulfillment & tracking",
        where: "shopify → track_order",
        why: "Returns carrier, tracking number and tracking URL so the agent resolves shipping questions without a human.",
    },
    {
        anchor: "data-email",
        field: "Email address",
        where: "shopify → list_orders / get_customers",
        why: "The lookup key a shopper gives to find their own orders, and the field matched against the order before anything is disclosed. Merchants can restrict it to the signed-in shopper.",
    },
    {
        anchor: "data-phone",
        field: "Phone number",
        where: "shopify → track_order · outbound notifications",
        why: "Gates disclosure — a tracking lookup is refused when the supplied phone does not match the order. Also the number merchant-configured notifications dial, under quiet hours, a 24h dedupe window and DLT/TCPA consent rules.",
    },
    {
        anchor: "data-name",
        field: "Name",
        where: "Agent console · customer context",
        why: "Lets the AI and the human agent taking over address the shopper correctly and confirm they have the right record.",
    },
    {
        anchor: "data-address",
        field: "Shipping address",
        where: "shopify → get_order",
        why: "Answers \"where is it going\" and resolves delivery exceptions — a wrong city or undeliverable postcode cannot be corrected unless the agent can read the address back.",
    },
    {
        anchor: "data-default-address",
        field: "Customer default address",
        where: "shopify → get_customers",
        why: "Country and region only, so shipping times, duties and the applicable return address are answered for the shopper's own locale.",
    },
    {
        anchor: "data-billing-address",
        field: "Billing address",
        where: "Not read or stored",
        why: "The order mirror has no billing-address field and the bulk order query never selects one. The single touch is reading billing_address.phone as the last fallback when an outbound notification has no other number for the customer; no billing address field is persisted or displayed.",
    },
    {
        anchor: "data-history",
        field: "Order count & amount spent",
        where: "Agent console · customer context",
        why: "Lets a human agent gauge account standing before applying a goodwill or returns decision.",
    },
    {
        anchor: "data-customer-id",
        field: "Shopify customer ID",
        where: "Internal join key only",
        why: "Never shown to the AI or to staff. Links the Shopify record to ours across an email change, and is the selector used to execute a customers/redact deletion reliably.",
    },
];

// Scopes the manifest deliberately omits. Listed so this page can be diffed
// against shopify.app.toml without the gaps reading as undisclosed access.
const unusedScopes = [
    { scope: "read_all_orders", why: "Not needed. The agent answers questions about current orders, which the default 60-day window covers, and everything placed after install arrives through the orders/* webhooks. No date-filtered historical query exists in the codebase. Not in the OAuth grant and not requested through the Partner Dashboard." },
    { scope: "write_orders", why: "No code path mutates an order. Cancel and refund intents open a human ticket instead." },
    { scope: "write_themes", why: "The app embed is read to detect activation; it is never written." },
    { scope: "read_shipping", why: "No shipping-rate query exists — shipping answers come from fulfillment tracking." },
    { scope: "read_analytics", why: "Chat ROI metrics are computed from FloGPT's own conversation records." },
    { scope: "read_purchase_options", why: "Product reads never select selling-plan or subscription fields." },
    { scope: "read_product_feeds", why: "The catalogue mirror uses the standard products connection." },
    { scope: "read_own_subscription_contracts", why: "FloGPT is not a subscription-commerce app. It never reads a customer subscription contract; there is no subscriptionContract query in the codebase." },
    { scope: "write_own_subscription_contracts", why: "No subscription contract is ever created, modified or cancelled. No subscriptionContract mutation exists in the codebase." },
    { scope: "write_customer_payment_methods", why: "FloGPT never stores, vaults or updates a customer payment method. No customerPaymentMethod mutation exists in the codebase." },
];

// Must mirror [[webhooks.subscriptions]] in shopify.app.toml — a reviewer
// diffing this page against the manifest reads either direction as a gap.
const webhookTopics = [
    { topic: "app/uninstalled", purpose: "Uninstall cleanup — marks the merchant inactive in FloGPT" },
    { topic: "app/scopes_update", purpose: "Re-syncs scopes after a permissions change" },
    { topic: "app_subscriptions/update", purpose: "Legacy Billing API plan changes (pre-App-Pricing subscriptions)" },
    { topic: "orders/create", purpose: "Powers high-value order confirmation calls" },
    { topic: "orders/updated", purpose: "Keeps order state in sync for in-call lookups" },
    { topic: "orders/paid", purpose: "Marks the order payable before any confirmation outreach" },
    { topic: "orders/cancelled", purpose: "Suppresses follow-up triggers and updates state" },
    { topic: "orders/fulfilled", purpose: "Powers proactive shipment-update calls" },
    { topic: "orders/partially_fulfilled", purpose: "Split-shipment state for \"where is my order\" answers" },
    { topic: "fulfillments/create", purpose: "Tracking numbers land in the mirror as carriers push them" },
    { topic: "fulfillments/update", purpose: "Shipment status changes, including cancelled fulfillments" },
    { topic: "fulfillment_events/create", purpose: "Carrier scan timeline — in transit, out for delivery, delivered" },
    { topic: "refunds/create", purpose: "Answers \"did my refund go through\" without an API hop" },
    { topic: "returns/*", purpose: "Return lifecycle (request, approve, decline, cancel, close, reopen, update)" },
    { topic: "products/*", purpose: "Catalog mirror for product and availability questions" },
    { topic: "inventory_levels/update", purpose: "Keeps \"is this in stock\" answers accurate" },
    { topic: "discounts/*", purpose: "Lets the agent quote currently-active promo codes" },
    { topic: "checkouts/*", purpose: "Abandoned-checkout recovery surface" },
    { topic: "shop/update", purpose: "Re-seeds store profile, pages and policies into the agent's grounding" },
    { topic: "bulk_operations/finish", purpose: "Backfill worker reacts on completion instead of polling" },
    { topic: "customers/create", purpose: "Onboarding flows and welcome SMS triggers" },
    { topic: "customers/update", purpose: "Customer context stays current for support agents" },
    { topic: "customers/delete", purpose: "Removes the customer reference from FloGPT" },
    { topic: "customers/data_request", purpose: "GDPR — packages customer data on request" },
    { topic: "customers/redact", purpose: "GDPR — purges customer PII from the order and customer mirrors" },
    { topic: "shop/redact", purpose: "GDPR — purges the entire shop record after uninstall" },
];

const outboundTriggers = [
    {
        title: "Cart Abandoned",
        topic: "carts/update",
        description: "Recovery SMS + outbound call once cart value crosses your minimum.",
        accent: "from-orange-500 to-pink-500",
    },
    {
        title: "Checkout Abandoned",
        topic: "checkouts/create",
        description: "Recover the sale while it's still warm — agent dials with cart context.",
        accent: "from-pink-500 to-rose-500",
    },
    {
        title: "High-Value Order",
        topic: "orders/create",
        description: "Confirmation call on high-AOV orders to verify intent and reduce chargebacks.",
        accent: "from-amber-500 to-orange-500",
    },
    {
        title: "Shipment Created",
        topic: "fulfillments/create",
        description: "Proactive \"your order is on the way\" call with live tracking link.",
        accent: "from-blue-500 to-cyan-500",
    },
    {
        title: "Refund Initiated",
        topic: "refunds/create",
        description: "Confirmation call when a refund is created — sets clear expectations.",
        accent: "from-emerald-500 to-teal-500",
    },
    {
        title: "Post-Delivery CSAT",
        topic: "time-relative",
        description: "Automated CSAT call N days after fulfillment — capture feedback and reviews.",
        accent: "from-indigo-500 to-purple-500",
    },
];

// Actions on the single `shopify` tool, mirroring ACTION_DESCRIPTIONS in
// backend/src/services/voice/shopifyTool.ts. The LLM sees one function whose
// `action` enum is narrowed to the actions the granted scopes permit.
const aiTools = [
    { name: "search_products", description: "Search the live catalog by keyword, filtered to in-stock items." },
    { name: "get_product", description: "Full details, variants, pricing and images for one product." },
    { name: "list_orders", description: "Find a shopper's own orders by their email or phone." },
    { name: "get_order", description: "Full details for one order by name (e.g. #1042) — line items, totals, status." },
    { name: "track_order", description: "Current shipping status with carrier and tracking URL, gated on a phone match." },
    { name: "get_customers", description: "Resolve a customer profile and order-history summary for support context." },
];

const troubleshooting = [
    {
        title: "Backend connection problem",
        symptom: "The embedded admin shows \"FloGPT can't reach the backend\".",
        cause: "INTERNAL_API_SECRET in the Shopify-app deploy env doesn't match the FloGPT backend's env, or the backend isn't reachable from the app.",
        fix: "Set INTERNAL_API_SECRET to the same value on both sides, redeploy, then reload the embedded admin — the merchant row re-syncs automatically.",
    },
    {
        title: "Storefront lookup not found",
        symptom: "Banner: \"Widget can't be resolved from your shop domain.\" The theme embed loads but no chat bubble appears.",
        cause: "The merchant row exists but the public /api/widget/shopify/by-domain endpoint returns 404 — usually a stale install on a renamed shop domain.",
        fix: "Open the embedded admin once to trigger a re-sync, or click \"Re-validate\" in the FloGPT integrations panel. If the shop's primary domain changed, the embed picks it up on the next load.",
    },
    {
        title: "Widget script bundles missing",
        symptom: "Banner: \"Widget JavaScript can't be loaded from your frontend.\"",
        cause: "The FloGPT frontend was deployed without a production build, so the widget bundles aren't present at the expected /widget/ URL.",
        fix: "Redeploy the FloGPT frontend with `npm run build` and confirm `/widget/flogpt-widget.js` and `/widget/flogpt-widget-ui.js` both return 200.",
    },
    {
        title: "Open Dashboard goes to /login",
        symptom: "Clicking \"Open Dashboard\" dumps you on a generic login screen instead of signing you in.",
        cause: "The SSO single-use code couldn't be minted, almost always cascading from a backend sync failure above.",
        fix: "Fix the backend connection first, reload the embedded admin, then click Open Dashboard again — the code is minted fresh on every load with a 60-second TTL.",
    },
    {
        title: "Webhook says 401",
        symptom: "Shopify's webhook console reports 401 Unauthorized for the FloGPT endpoint.",
        cause: "HMAC signature mismatch — the app's API secret was rotated, the raw request body was modified by a proxy, or the wrong webhook URL is registered.",
        fix: "Re-register webhooks from the embedded admin (it auto-reconciles on every load), and confirm no upstream proxy is gzipping or re-serializing the JSON body.",
    },
];

const primaryKeywords = [
    "FloGPT Shopify docs",
    "FloGPT Shopify app setup",
    "Shopify AI chat widget docs",
    "Shopify AI voice agent docs",
    "Shopify webhook integration FloGPT",
    "Shopify GDPR webhooks",
    "Shopify abandoned cart outbound call",
    "Shopify chat widget install guide",
];

export default function ShopifyDocsPage() {
    usePageSeo({
        title: "FloGPT for Shopify — Docs & Integration Guide | FloGPT",
        description:
            "Documentation for the FloGPT Shopify app: install, OAuth scopes, theme app embed, AI tools used during voice & chat, outbound triggers, webhooks, protected customer data usage, GDPR compliance and troubleshooting.",
        keywords: primaryKeywords.join(", "),
        canonical: "/docs/shopify",
    });

    const serviceSchema = useMemo(
        () =>
            buildServiceSchema({
                name: "FloGPT for Shopify — Integration Guide",
                description:
                    "Technical and operational documentation for the FloGPT Shopify app. Covers installation, OAuth scopes, theme app embed, in-call AI tools, Shopify webhook subscriptions, outbound triggers, GDPR compliance and troubleshooting.",
                path: "/docs/shopify",
                serviceType: "Shopify App Documentation",
                features: [
                    "Step-by-step Shopify app installation",
                    "Theme app embed activation",
                    "OAuth scope reference",
                    "In-call Shopify AI tool catalogue",
                    "Outbound trigger templates",
                    "Shopify webhook topic reference",
                    "Protected customer data field-level justification",
                    "GDPR data request and redaction flow",
                    "Troubleshooting common deploy issues",
                ],
            }),
        [],
    );

    const techArticleSchema = useMemo(
        () => ({
            "@context": "https://schema.org",
            "@type": "TechArticle",
            headline: "FloGPT for Shopify — Integration Guide",
            description:
                "Install, configure, and operate the FloGPT Shopify app: OAuth scopes, storefront widget, AI tools, webhooks, GDPR and troubleshooting.",
            url: `${marketingOrigin()}/docs/shopify`,
            inLanguage: "en",
            datePublished: "2026-05-14",
            dateModified: "2026-10-06",
            proficiencyLevel: "Beginner",
            audience: { "@type": "Audience", audienceType: "Shopify merchants and developers" },
            about: [
                { "@type": "Thing", name: "Shopify" },
                { "@type": "Thing", name: "FloGPT" },
                { "@type": "Thing", name: "AI chat widget" },
                { "@type": "Thing", name: "AI voice agent" },
            ],
        }),
        [],
    );

    return (
        <div className="bg-slate-950 text-slate-100 min-h-screen">
            <LandingNavbar />

            <main>
                {/* HERO */}
                <section className="relative overflow-hidden border-b border-slate-900 pt-24 sm:pt-28" id="overview">
                    <div className="absolute inset-0 -z-10">
                        <div className="absolute -top-24 left-1/2 h-80 w-80 -translate-x-1/2 rounded-full bg-orange-500/20 blur-3xl" />
                        <div className="absolute right-0 top-24 h-96 w-96 rounded-full bg-pink-500/10 blur-3xl" />
                    </div>
                    <div className="mx-auto max-w-5xl px-6 lg:px-8 py-16 lg:py-20">
                        <motion.div
                            initial={{ opacity: 0, y: 18 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            className="text-center"
                        >
                            <nav aria-label="Breadcrumb" className="mb-4 flex justify-center text-xs text-slate-400">
                                <ol className="flex items-center gap-2">
                                    <li>
                                        <Link to="/docs" className="transition hover:text-white">
                                            Docs
                                        </Link>
                                    </li>
                                    <li aria-hidden="true">/</li>
                                    <li className="text-slate-300">Shopify</li>
                                </ol>
                            </nav>
                            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-slate-700/60 bg-slate-800/40 px-3 py-1 text-xs font-medium text-slate-300">
                                <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-orange-500 to-pink-500" />
                                Docs · Shopify Integration · App Version 2026-01
                            </div>
                            <h1 className="text-4xl font-bold leading-tight text-white sm:text-5xl lg:text-[3.1rem] lg:leading-[1.1]">
                                FloGPT for{" "}
                                <motion.span
                                    className="inline-block"
                                    animate={{ backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"] }}
                                    transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                                    style={{
                                        background: "linear-gradient(90deg, #f97316, #ec4899, #3b82f6, #f97316)",
                                        backgroundSize: "200% 100%",
                                        WebkitBackgroundClip: "text",
                                        WebkitTextFillColor: "transparent",
                                        backgroundClip: "text",
                                    }}
                                >
                                    Shopify
                                </motion.span>
                                {" "}— Integration Guide
                            </h1>
                            <p className="mx-auto mt-5 max-w-3xl text-lg text-slate-300 leading-relaxed">
                                Everything you need to install, configure, and operate the FloGPT Shopify app. Storefront chat widget, AI voice agent, in-call Shopify tools, outbound triggers, webhooks and GDPR — all in one place.
                            </p>

                            <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
                                <a
                                    href="#install"
                                    className="inline-flex rounded-xl bg-gradient-to-r from-orange-500 to-pink-500 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-orange-500/20 transition-transform hover:-translate-y-0.5"
                                >
                                    Start with installation
                                </a>
                                <Link
                                    to="/shopify-ai-customer-support"
                                    className="inline-flex items-center justify-center rounded-xl border border-white/15 bg-white/5 px-6 py-3 text-sm font-semibold text-white backdrop-blur transition hover:bg-white/10"
                                >
                                    Product overview
                                </Link>
                                <Link
                                    to="/docs"
                                    className="inline-flex items-center justify-center rounded-xl border border-white/15 bg-white/5 px-6 py-3 text-sm font-semibold text-white backdrop-blur transition hover:bg-white/10"
                                >
                                    All documentation
                                </Link>
                            </div>
                            <div className="mt-5 flex flex-wrap justify-center gap-x-6 gap-y-2 text-xs text-slate-400">
                                <span>· Read-only Shopify scopes</span>
                                <span>· HMAC-verified webhooks</span>
                                <span>· GDPR / DPDP compliant</span>
                            </div>
                        </motion.div>
                    </div>
                </section>

                {/* CONTENT GRID — sticky TOC on desktop, inlined on mobile */}
                <div className="mx-auto max-w-7xl px-6 lg:px-8 py-12 lg:py-16">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                        <aside className="lg:col-span-3">
                            <div className="lg:sticky lg:top-24">
                                <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
                                    <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-3">
                                        On this page
                                    </div>
                                    <nav>
                                        <ul className="space-y-1.5 text-sm">
                                            {tocLinks.map((link) => (
                                                <li key={link.id}>
                                                    <a
                                                        href={`#${link.id}`}
                                                        className="flex items-start gap-2.5 rounded-lg px-2.5 py-1.5 text-slate-300 hover:bg-slate-800/80 hover:text-white transition-colors"
                                                    >
                                                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gradient-to-r from-orange-500 to-pink-500" />
                                                        <span>{link.label}</span>
                                                    </a>
                                                </li>
                                            ))}
                                        </ul>
                                    </nav>
                                </div>
                            </div>
                        </aside>

                        <div className="lg:col-span-9 space-y-14">
                            {/* 1. INSTALL */}
                            <DocsSection id="install" eyebrow="Step 1" title="Install the FloGPT Shopify app">
                                <p>
                                    FloGPT is distributed as an embedded Shopify app. Open the FloGPT listing on the Shopify App Store, click <strong>Add app</strong>, and approve the requested OAuth scopes. Shopify redirects you straight into the embedded FloGPT admin.
                                </p>
                                <ol className="mt-5 space-y-3 text-slate-300 list-decimal list-inside marker:text-orange-400">
                                    <li>Open the Shopify App Store and search for <strong>FloGPT</strong>.</li>
                                    <li>Click <strong>Add app</strong>. Shopify prompts you for the OAuth scope grant.</li>
                                    <li>
                                        On first install, FloGPT auto-creates a Shopify-scoped account for your store using <code className="rounded bg-slate-800/80 px-1.5 py-0.5 text-[12px] text-slate-200">&lt;shop&gt;@shopify-merchant.flogpt.com</code>. You don't need to register separately.
                                    </li>
                                    <li>
                                        The app provisions a unique <strong>widget ID</strong> and registers webhooks declaratively from <code className="rounded bg-slate-800/80 px-1.5 py-0.5 text-[12px] text-slate-200">shopify.app.toml</code>.
                                    </li>
                                </ol>
                                <Callout tone="info" title="Embedded vs. full dashboard">
                                    The embedded admin inside Shopify is enough for most day-to-day tasks: enable the storefront widget, customize copy and appearance, and check the integration health. For knowledge-base, voice agents, workflows and analytics, click <em>Open Dashboard</em> to SSO into the full FloGPT app.
                                </Callout>
                            </DocsSection>

                            {/* 2. OAUTH SCOPES */}
                            <DocsSection id="scopes" eyebrow="Step 2" title="OAuth scopes requested by FloGPT">
                                <p>
                                    FloGPT requests <strong>read-only</strong> scopes for the data the AI agent needs to answer support questions and recommend products. Refund and cancel actions never happen silently — they route to a human ticket with the call or chat transcript attached.
                                </p>
                                <div className="mt-6 overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/60">
                                    <table className="w-full text-left text-sm">
                                        <thead className="bg-slate-900/80 text-[11px] uppercase tracking-wider text-slate-400">
                                            <tr>
                                                <th className="px-4 py-3 font-semibold">Scope</th>
                                                <th className="px-4 py-3 font-semibold">What FloGPT does with it</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-800/80">
                                            {requestedScopes.map((row) => (
                                                <tr key={row.scope}>
                                                    <td className="px-4 py-3 align-top">
                                                        <code className="rounded bg-slate-800/80 px-1.5 py-0.5 text-[12px] text-orange-200">
                                                            {row.scope}
                                                        </code>
                                                        {row.pending && (
                                                            <span className="mt-1.5 block w-fit rounded-full border border-slate-600 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                                                                Next version
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 text-slate-300">
                                                        {row.what}
                                                        {row.pending && (
                                                            <span className="mt-1 block text-xs text-slate-500">
                                                                Not in the currently published app version — no store has granted it.
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <Callout tone="warn" title="No write scopes by default">
                                    The FloGPT app does <strong>not</strong> request write scopes for orders, refunds, fulfillments or inventory. If a customer asks for a refund, the AI opens a high-priority human ticket with the full transcript so your team can apply your store policy.
                                </Callout>
                            </DocsSection>

                            {/* 3. THEME APP EMBED */}
                            <DocsSection id="embed" eyebrow="Step 3" title="Enable the storefront chat widget">
                                <p>
                                    The chat widget installs as a <strong>theme app embed</strong>. No theme code changes are required and you can toggle the widget on or off without touching Liquid.
                                </p>
                                <ol className="mt-5 space-y-3 text-slate-300 list-decimal list-inside marker:text-orange-400">
                                    <li>
                                        From the embedded FloGPT admin, click <strong>Open theme editor</strong>. The link deep-links straight to your live theme's <em>App embeds</em> drawer with the FloGPT embed pre-selected.
                                    </li>
                                    <li>
                                        Toggle <strong>FloGPT Chat Widget</strong> to <em>on</em>.
                                    </li>
                                    <li>
                                        Click <strong>Save</strong>. Visit your storefront — the chat bubble should appear in the configured corner within seconds.
                                    </li>
                                </ol>
                                <Callout tone="info" title="Headless storefronts (Hydrogen, Next.js, custom React)">
                                    The theme app embed only works on storefronts rendered by Shopify themes. For headless storefronts, open the FloGPT dashboard (<strong>Open Dashboard</strong> in the embedded admin), go to <strong>Chat Widgets</strong>, select your widget and copy its embed code into your frontend — it works on any framework.
                                </Callout>
                                <p className="mt-5">
                                    The embed automatically detects your shop domain and the widget loads its configuration from FloGPT — no widget ID needs to be hardcoded into the theme.
                                </p>
                            </DocsSection>

                            {/* 4. SSO DASHBOARD */}
                            <DocsSection id="dashboard" eyebrow="Step 4" title="Open the full FloGPT dashboard">
                                <p>
                                    The embedded admin covers widget toggles and customization. For everything else — knowledge base, voice agents, call pipelines, workflows, analytics — click <strong>Open Dashboard</strong>.
                                </p>
                                <ul className="mt-5 space-y-3 text-slate-300">
                                    <li className="flex items-start gap-3">
                                        <Check />
                                        <span>
                                            The button mints a fresh <strong>single-use SSO code</strong> on every page load with a 60-second TTL.
                                        </span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <Check />
                                        <span>
                                            It redirects to <code className="rounded bg-slate-800/80 px-1.5 py-0.5 text-[12px] text-orange-200">/auth/shopify-sso?code=&hellip;</code> on the FloGPT frontend, which exchanges the code for a session.
                                        </span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <Check />
                                        <span>
                                            If the SSO code can't be minted, the button is replaced with a clear diagnostic banner — you'll never end up on a generic login screen.
                                        </span>
                                    </li>
                                </ul>
                            </DocsSection>

                            {/* 5. CUSTOMIZE */}
                            <DocsSection id="customize" eyebrow="Step 5" title="Customize the widget">
                                <div className="grid gap-5 sm:grid-cols-2">
                                    <article className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
                                        <div className="text-[11px] font-semibold uppercase tracking-wider text-emerald-300">Inside Shopify</div>
                                        <h3 className="mt-1.5 text-lg font-semibold text-white">Quick settings</h3>
                                        <ul className="mt-3 space-y-2 text-sm text-slate-300">
                                            <li>· Theme (light, dark, auto)</li>
                                            <li>· Launcher position</li>
                                            <li>· Widget title</li>
                                            <li>· Welcome message and greeting</li>
                                            <li>· Input placeholder text</li>
                                            <li>· Active / inactive toggle</li>
                                        </ul>
                                    </article>
                                    <article className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
                                        <div className="text-[11px] font-semibold uppercase tracking-wider text-orange-300">In the full dashboard</div>
                                        <h3 className="mt-1.5 text-lg font-semibold text-white">Advanced customization</h3>
                                        <ul className="mt-3 space-y-2 text-sm text-slate-300">
                                            <li>· Brand colors and gradients</li>
                                            <li>· Custom CSS and fonts</li>
                                            <li>· AI system prompts</li>
                                            <li>· Knowledge-base sources</li>
                                            <li>· Workflows and triggers</li>
                                            <li>· Analytics and transcripts</li>
                                        </ul>
                                    </article>
                                </div>
                            </DocsSection>

                            {/* 6. AI TOOLS */}
                            <DocsSection id="tools" eyebrow="Reference" title="AI tools in voice & chat">
                                <p>
                                    During a call or chat, the FloGPT agent reaches Shopify through a single <code className="rounded bg-slate-800/80 px-1.5 py-0.5 text-[12px] text-orange-200">shopify</code> tool whose <code>action</code> list is narrowed to the scopes you've granted — the LLM is never shown an action it has no permission to run.
                                </p>
                                <div className="mt-6 grid gap-3 sm:grid-cols-2">
                                    {aiTools.map((tool) => (
                                        <div
                                            key={tool.name}
                                            className="rounded-xl border border-slate-800 bg-slate-900/60 p-4"
                                        >
                                            <code className="rounded bg-slate-800/80 px-1.5 py-0.5 text-[12px] font-mono text-orange-200">
                                                {tool.name}
                                            </code>
                                            <p className="mt-2 text-sm text-slate-300 leading-relaxed">{tool.description}</p>
                                        </div>
                                    ))}
                                </div>
                                <Callout tone="info" title="Refund-safe by default">
                                    Cancellation and refund intents are not in the action list for App Store installs — <code>write_orders</code> isn't requested, so the action is never offered to the model. The agent captures the intent, summarizes it and escalates to a human with the transcript attached rather than promising a refund.
                                </Callout>
                            </DocsSection>

                            {/* 7. OUTBOUND TRIGGERS */}
                            <DocsSection id="triggers" eyebrow="Reference" title="Outbound trigger templates">
                                <p>
                                    Outbound calls fire from Shopify webhooks and time-relative cadences. Each trigger ships with a Shopify webhook subscription, a dedupe key, a phone resolver and a default call script — drop it into a pipeline and the agent starts dialing.
                                </p>
                                <div className="mt-6 grid gap-4 sm:grid-cols-2">
                                    {outboundTriggers.map((trigger) => (
                                        <article
                                            key={trigger.title}
                                            className="group relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/60 p-5"
                                        >
                                            <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${trigger.accent}`} />
                                            <div className="flex items-center justify-between">
                                                <h3 className="text-base font-semibold text-white">{trigger.title}</h3>
                                                <code className="rounded-md border border-slate-700 bg-slate-950/60 px-2 py-0.5 text-[10px] font-mono text-slate-300">
                                                    {trigger.topic}
                                                </code>
                                            </div>
                                            <p className="mt-3 text-sm text-slate-300 leading-relaxed">{trigger.description}</p>
                                        </article>
                                    ))}
                                </div>
                                <Callout tone="info" title="Per-trigger guardrails">
                                    Every trigger respects per-region quiet hours, a deduplication window (so the same customer isn't called twice in 24h), DLT / TCPA consent rules, and filters such as <code>minOrderValue</code>, <code>minCartValue</code>, <code>tags</code>, and <code>productIds</code>.
                                </Callout>
                                <Callout tone="warn" title="Cart abandonment needs a custom-app connection">
                                    <code>carts/update</code> is not among the App Store app's webhook subscriptions. The Cart Abandoned trigger therefore only fires for stores connected with their own custom-app credentials. On an App Store install, use <strong>Checkout Abandoned</strong> (<code>checkouts/create</code>) instead.
                                </Callout>
                            </DocsSection>

                            {/* 8. WEBHOOKS */}
                            <DocsSection id="webhooks" eyebrow="Reference" title="Webhooks & data flow">
                                <p>
                                    Webhook subscriptions are declared in <code className="rounded bg-slate-800/80 px-1.5 py-0.5 text-[12px] text-slate-200">shopify.app.toml</code> (the App Store source of truth) and reconciled on every page load of the embedded admin. The API version is pinned to <code>2026-01</code>.
                                </p>
                                <div className="mt-6 overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/60">
                                    <table className="w-full text-left text-sm">
                                        <thead className="bg-slate-900/80 text-[11px] uppercase tracking-wider text-slate-400">
                                            <tr>
                                                <th className="px-4 py-3 font-semibold">Topic</th>
                                                <th className="px-4 py-3 font-semibold">Purpose</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-800/80">
                                            {webhookTopics.map((row) => (
                                                <tr key={row.topic}>
                                                    <td className="px-4 py-3 align-top">
                                                        <code className="rounded bg-slate-800/80 px-1.5 py-0.5 text-[12px] text-orange-200">
                                                            {row.topic}
                                                        </code>
                                                    </td>
                                                    <td className="px-4 py-3 text-slate-300">{row.purpose}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <Callout tone="warn" title="HMAC verification on every request">
                                    Every incoming webhook is verified with a timing-safe HMAC comparison against the raw request body. Requests with a missing or invalid signature are rejected with <code>401</code> before any business logic runs.
                                </Callout>
                            </DocsSection>

                            {/* 9. PROTECTED CUSTOMER DATA */}
                            <DocsSection
                                id="protected-customer-data"
                                eyebrow="Compliance"
                                title="Protected customer data — what we use and why"
                            >
                                <p>
                                    Shopify permits access to protected customer data only where it delivers app functionality. Every field FloGPT reads is listed below against the feature that consumes it. Access is read-only throughout.
                                </p>
                                <div className="mt-6 overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/60">
                                    <table className="w-full text-left text-sm">
                                        <thead className="bg-slate-900/80 text-[11px] uppercase tracking-wider text-slate-400">
                                            <tr>
                                                <th className="px-4 py-3 font-semibold">Field</th>
                                                <th className="px-4 py-3 font-semibold">Used by</th>
                                                <th className="px-4 py-3 font-semibold">Why it's required</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-800/80">
                                            {/* The anchor sits on a block inside the cell rather than on the
                                                row: scroll-margin is unreliable on table-row boxes, and a
                                                deep-linked row landing under the fixed header is the whole
                                                problem the anchors exist to solve. Row highlighting is a
                                                nicety — without `has-` support it degrades to no highlight
                                                and the anchor still lands. */}
                                            {protectedDataUses.map((row) => (
                                                <tr key={row.anchor} className="transition-colors has-[span:target]:bg-orange-500/10">
                                                    <td className="px-4 py-3 align-top font-medium text-white">
                                                        <span id={row.anchor} className="block scroll-mt-28">
                                                            {row.field}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3 align-top">
                                                        <code className="rounded bg-slate-800/80 px-1.5 py-0.5 text-[12px] text-orange-200">
                                                            {row.where}
                                                        </code>
                                                    </td>
                                                    <td className="px-4 py-3 text-slate-300">{row.why}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                <h3 className="mt-10 text-lg font-semibold text-white">Minimization is enforced in product, not policy</h3>
                                <ul className="mt-4 space-y-3 text-slate-300">
                                    <li className="flex items-start gap-3">
                                        <Check />
                                        <span>
                                            <strong>Per-field opt-in.</strong> You choose, per action, which fields the AI is allowed to receive. Unselected fields are stripped before the response reaches the model — not hidden in the UI afterwards.
                                        </span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <Check />
                                        <span>
                                            <strong>Self-service only.</strong> Switch on <em>Require authenticated user email</em> for order listing and <em>Restrict to authenticated user</em> for customer lookup, and the agent can only ever retrieve the signed-in shopper's own records — arbitrary lookups become impossible.
                                        </span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <Check />
                                        <span>
                                            <strong>Ownership check before disclosure.</strong> Order tracking matches the caller's phone against the phone on the order and refuses the lookup on a mismatch. Order-name matching is exact, so <code>#100</code> cannot surface <code>#1001</code>.
                                        </span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <Check />
                                        <span>
                                            <strong>Capped result sets.</strong> At most 10 orders or 5 customer records per lookup, so no path enumerates your customer list.
                                        </span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <Check />
                                        <span>
                                            <strong>Transcript redaction.</strong> Opt-in masking removes email, phone, card number, SSN, IP and street address from chat and call transcripts before they are stored.
                                        </span>
                                    </li>
                                </ul>

                                <h3 className="mt-10 text-lg font-semibold text-white">Deletion behaviour</h3>
                                <p className="mt-3">
                                    On <code className="rounded bg-slate-800/80 px-1.5 py-0.5 text-[12px] text-orange-200">customers/redact</code>, every materialized PII field on the customer and order mirrors is nulled and the raw Shopify payload is removed outright. Non-PII accounting fields (totals, currency, financial status) are kept so your bookkeeping survives the deletion. On <code className="rounded bg-slate-800/80 px-1.5 py-0.5 text-[12px] text-orange-200">shop/redact</code> all orders, customers, the merchant record and the OAuth session are deleted, and stored webhook payloads for the shop are scrubbed — only non-PII audit metadata proving the request was honoured remains.
                                </p>

                                <h3 id="scopes-not-requested" className="mt-10 scroll-mt-28 text-lg font-semibold text-white">
                                    Scopes FloGPT does <span className="text-rose-300">not</span> request
                                </h3>
                                <p className="mt-3">
                                    None of the scopes below are in the OAuth grant, and none are requested through the Partner Dashboard. They are listed only so this page can be diffed against the manifest without the gaps reading as undisclosed access.
                                </p>
                                {/* Struck, rose-tinted and badged rather than sharing the orange code
                                    chip of the requested-scopes table above: an app reviewer skimming
                                    for a scope name reads the chip, not the heading, and read this
                                    grid as a second list of things we ask for. */}
                                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                                    {unusedScopes.map((row) => (
                                        <div key={row.scope} className="rounded-xl border border-rose-500/20 bg-rose-500/[0.04] p-4">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <code className="rounded bg-slate-800/60 px-1.5 py-0.5 text-[12px] text-slate-400 line-through decoration-rose-400/60">
                                                    {row.scope}
                                                </code>
                                                <span className="rounded-full border border-rose-500/30 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-rose-300">
                                                    Not requested
                                                </span>
                                            </div>
                                            <p className="mt-2 text-sm text-slate-400 leading-relaxed">{row.why}</p>
                                        </div>
                                    ))}
                                </div>
                                <h3 id="billing-scopes" className="mt-10 scroll-mt-28 text-lg font-semibold text-white">
                                    How merchants pay for FloGPT
                                </h3>
                                {/* Retitled away from "Billing and subscription scopes": an app reviewer
                                    checking the customer-subscription requirement read a section about
                                    the merchant's own plan as an answer about subscription contracts. */}
                                <Callout tone="warn" title="Two unrelated things both called a subscription">
                                    This section is about the <strong>app subscription</strong> — the plan a merchant buys from FloGPT. It is not about <strong>subscription contracts</strong>, the recurring orders a store sells to its shoppers. FloGPT is not a subscription-commerce app: it never creates, reads or modifies a subscription contract, and never touches a customer payment method. Those scopes are listed above as not requested.
                                </Callout>
                                <p className="mt-5">
                                    FloGPT bills through <strong>Shopify App Pricing</strong>, with plans defined in the Partner Dashboard. App Pricing apps are refused the Billing API outright, so the app never calls <code className="rounded bg-slate-800/80 px-1.5 py-0.5 text-[12px] text-orange-200">appSubscriptionCreate</code>, <code className="rounded bg-slate-800/80 px-1.5 py-0.5 text-[12px] text-orange-200">appUsageRecordCreate</code>, <code className="rounded bg-slate-800/80 px-1.5 py-0.5 text-[12px] text-orange-200">appSubscriptionLineItemUpdate</code> or <code className="rounded bg-slate-800/80 px-1.5 py-0.5 text-[12px] text-orange-200">appSubscriptionCancel</code>. Merchants subscribe on Shopify's hosted plan page and the app observes the result: the only billing read is <code className="rounded bg-slate-800/80 px-1.5 py-0.5 text-[12px] text-orange-200">currentAppInstallation.activeSubscriptions</code>, which is the app's own installation data and needs no access scope. Metered usage is reported through the App Events API and priced by Shopify from the dashboard meters.
                                </p>
                                <p className="mt-3">
                                    The <code className="rounded bg-slate-800/80 px-1.5 py-0.5 text-[12px] text-orange-200">app_subscriptions/update</code> webhook subscription is retained solely for merchants whose <em>app plan</em> predates the move to App Pricing — those continue to bill through the Billing API and continue to emit it. It carries no shopper data and is unrelated to subscription contracts.
                                </p>
                                <Callout tone="warn" title="No training on your customers' data">
                                    Shopify data retrieved during a conversation is used to answer that conversation. It is never used to train or fine-tune models, and is never shared across merchants — all storage is scoped to a single organization and a single shop domain.
                                </Callout>
                            </DocsSection>

                            {/* 10. PRIVACY & GDPR */}
                            <DocsSection id="privacy" eyebrow="Compliance" title="Privacy, GDPR & data retention">
                                <p>
                                    FloGPT implements Shopify's mandatory compliance webhooks plus additional opt-in privacy controls for chat and call transcripts.
                                </p>
                                <div className="mt-5 grid gap-4 sm:grid-cols-3">
                                    <article className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
                                        <code className="rounded bg-slate-800/80 px-1.5 py-0.5 text-[11px] text-orange-200">
                                            customers/data_request
                                        </code>
                                        <p className="mt-3 text-sm text-slate-300 leading-relaxed">
                                            A merchant request is logged and any customer data FloGPT has stored is packaged for delivery within the documented response window.
                                        </p>
                                    </article>
                                    <article className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
                                        <code className="rounded bg-slate-800/80 px-1.5 py-0.5 text-[11px] text-orange-200">
                                            customers/redact
                                        </code>
                                        <p className="mt-3 text-sm text-slate-300 leading-relaxed">
                                            Customer chat transcripts, call transcripts and stored profile fields are purged after the documented retention window so no PII remains tied to that customer.
                                        </p>
                                    </article>
                                    <article className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
                                        <code className="rounded bg-slate-800/80 px-1.5 py-0.5 text-[11px] text-orange-200">
                                            shop/redact
                                        </code>
                                        <p className="mt-3 text-sm text-slate-300 leading-relaxed">
                                            48 hours after a shop uninstalls FloGPT, the entire shop record (merchant profile, encrypted creds, derived caches) is purged.
                                        </p>
                                    </article>
                                </div>
                                <Callout tone="info" title="Additional privacy controls">
                                    Opt-in PII redaction on transcripts can mask email, phone, credit card, SSN, IP and street address before storage. Data residency can be region-locked (EU, IN, US) for plans on annual or above. See the <Link to="/privacy" className="text-orange-300 hover:text-orange-200 underline underline-offset-2">Privacy Policy</Link> for the full retention schedule.
                                </Callout>
                            </DocsSection>

                            {/* 11. TROUBLESHOOTING */}
                            <DocsSection id="troubleshooting" eyebrow="Operations" title="Troubleshooting">
                                <p>
                                    The embedded admin runs four health probes on every load — backend sync, SSO code minting, public widget lookup, and widget asset CDN — and surfaces a diagnostic banner the moment one of them fails. The most common issues and their fixes:
                                </p>
                                <div className="mt-6 space-y-4">
                                    {troubleshooting.map((item) => (
                                        <article
                                            key={item.title}
                                            className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5"
                                        >
                                            <h3 className="text-base font-semibold text-white">{item.title}</h3>
                                            <dl className="mt-3 space-y-2 text-sm">
                                                <div>
                                                    <dt className="text-[11px] font-semibold uppercase tracking-wider text-pink-300">Symptom</dt>
                                                    <dd className="mt-1 text-slate-300 leading-relaxed">{item.symptom}</dd>
                                                </div>
                                                <div>
                                                    <dt className="text-[11px] font-semibold uppercase tracking-wider text-amber-300">Cause</dt>
                                                    <dd className="mt-1 text-slate-300 leading-relaxed">{item.cause}</dd>
                                                </div>
                                                <div>
                                                    <dt className="text-[11px] font-semibold uppercase tracking-wider text-emerald-300">Fix</dt>
                                                    <dd className="mt-1 text-slate-300 leading-relaxed">{item.fix}</dd>
                                                </div>
                                            </dl>
                                        </article>
                                    ))}
                                </div>
                            </DocsSection>
                        </div>
                    </div>
                </div>

                {/* FAQ */}
                <div id="faq">
                    <FAQSection
                        id="shopify-docs"
                        eyebrow="Shopify · Docs FAQ"
                        heading="Frequently asked questions"
                        items={SHOPIFY_DOCS_FAQS}
                    />
                </div>

                {/* CTA */}
                <section className="relative overflow-hidden border-t border-slate-900 bg-slate-950/60">
                    <div className="absolute inset-0 -z-10 opacity-30">
                        <div className="pointer-events-none absolute -left-40 top-10 h-72 w-72 rounded-full bg-orange-500/20 blur-3xl" />
                        <div className="pointer-events-none absolute -right-40 bottom-10 h-72 w-72 rounded-full bg-pink-500/15 blur-3xl" />
                    </div>
                    <div className="mx-auto max-w-5xl px-6 lg:px-8 py-16 lg:py-20">
                        <div className="text-center max-w-3xl mx-auto">
                            <h2 className="text-3xl sm:text-4xl font-bold text-white">Ready to plug FloGPT into your Shopify store?</h2>
                            <p className="mt-4 text-lg text-slate-300 leading-relaxed">
                                Install the app, enable the theme embed, and let the AI agent handle order lookups, refunds, returns and recovery calls 24/7 — across phone and chat.
                            </p>
                            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
                                <Link
                                    to="/shopify-ai-customer-support#book-free-demo"
                                    className="inline-flex rounded-xl bg-gradient-to-r from-orange-500 to-pink-500 px-7 py-3 text-sm font-semibold text-white shadow-lg shadow-orange-500/20 transition-transform hover:-translate-y-0.5"
                                >
                                    Book a Free Demo
                                </Link>
                                <Link
                                    to="/shopify-ai-customer-support"
                                    className="inline-flex items-center justify-center rounded-xl border border-white/15 bg-white/5 px-7 py-3 text-sm font-semibold text-white backdrop-blur transition hover:bg-white/10"
                                >
                                    See product overview
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>
            </main>

            <Footer />

            <JsonLd id="service-shopify-docs" data={serviceSchema} />
            <JsonLd id="techarticle-shopify-docs" data={techArticleSchema} />
        </div>
    );
}

/**
 * Section wrapper used throughout the docs page. Pulled out so each section's
 * heading, eyebrow, anchor target and scroll-margin stay consistent — and so
 * future sections can be added without re-pasting the boilerplate.
 */
const DocsSection = ({
    id,
    eyebrow,
    title,
    children,
}: {
    id: string;
    eyebrow?: string;
    title: string;
    children: React.ReactNode;
}) => (
    <section id={id} className="scroll-mt-24">
        <motion.div
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true, amount: 0.2 }}
        >
            {eyebrow && (
                <div className="inline-flex items-center gap-2 rounded-full border border-slate-700/60 bg-slate-800/40 px-2.5 py-1 text-[11px] font-medium text-slate-300">
                    <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-orange-500 to-pink-500" />
                    {eyebrow}
                </div>
            )}
            <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-white">{title}</h2>
            <div className="mt-5 text-slate-300 leading-relaxed space-y-4">{children}</div>
        </motion.div>
    </section>
);

const Callout = ({
    tone = "info",
    title,
    children,
}: {
    tone?: "info" | "warn";
    title: string;
    children: React.ReactNode;
}) => {
    const palette =
        tone === "warn"
            ? "border-amber-500/30 bg-amber-500/5"
            : "border-orange-500/30 bg-orange-500/5";
    const eyebrow = tone === "warn" ? "text-amber-200" : "text-orange-200";
    return (
        <aside className={`mt-5 rounded-2xl border ${palette} p-5`}>
            <div className={`text-[11px] font-semibold uppercase tracking-wider ${eyebrow}`}>
                {tone === "warn" ? "Heads up" : "Note"}
            </div>
            <h4 className="mt-1 text-base font-semibold text-white">{title}</h4>
            <div className="mt-2 text-sm leading-relaxed text-slate-300">{children}</div>
        </aside>
    );
};

const Check = () => (
    <svg
        className="mt-0.5 h-5 w-5 shrink-0 text-emerald-400"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
    >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.4} d="M5 13l4 4L19 7" />
    </svg>
);
